import {
  AccessRoleResponse,
  AccountSummaryResponse,
  CasinoProfitLossResponse,
  EventProfitLossResponse,
  MembersResponse,
  Role,
  SportPermissionResponse,
  TransactionHistoryResponse,
  UpdateSportPermissionBody,
} from '@/types';
import apiSlice from './apiSlice';

export const downlineApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMembers: builder.query<
      MembersResponse,
      {
        username?: string;
        page?: number;
        limit?: number;
        rollId?: number;
        level?: number;
        fromDate?: string;
        toDate?: string;
        status?: string;
      }
    >({
      query: ({ username, page, limit, rollId, level, fromDate, toDate, status }) => ({
        url: 'users/me/sub-users',
        method: 'GET',
        params: {
          ...(username ? { username } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(rollId ? { rollId } : {}),
          ...(level ? { level } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(status ? { status } : {}),
          ts: Date.now(),
        },
      }),
      providesTags: ['Members'],
    }),
    getSubMembers: builder.query<
      MembersResponse,
      {
        username?: string;
        page?: number;
        limit?: number;
        level?: number;
        fromDate?: string;
        toDate?: string;
        userId?: string | undefined;
        status?: string;
        upline?: string;
        rollId?: number;
      }
    >({
      query: ({
        username,
        page,
        limit,
        level,
        fromDate,
        toDate,
        userId,
        status,
        upline,
        rollId,
      } = {}) => ({
        url: `/users/sub-users/${userId}`,
        method: 'GET',
        params: {
          ...(username ? { username } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(level ? { level } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(status ? { status } : {}),
          ...(upline ? { upline } : {}),
          ...(rollId ? { rollId } : {}),
          ts: Date.now(),
        },
      }),
    }),
    getAccountSummary: builder.query<AccountSummaryResponse, { userId: string | undefined }>({
      query: ({ userId }) => ({
        url: `/users/account/summary/${userId}`,
        method: 'GET',
        params: { ts: Date.now() },
      }),
    }),
    getCasinoDetail: builder.query<
      CasinoProfitLossResponse,
      { fromDate?: string; toDate?: string; userId: number }
    >({
      query: ({ userId, fromDate, toDate }) => ({
        url: `/casino/profit/loss/${userId}`,
        method: 'GET',
        params: {
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ts: Date.now(),
        },
      }),
    }),

    addCreditRefrence: builder.mutation<
      any,
      {
        amount: number;
        fromAccount: string;
        toAccount: string;
        userId: string;
        transactionCode: string;
      }
    >({
      query: ({ userId, amount, ...body }) => ({
        url: `/users/${userId}/wallets/give/credit-limit`,
        method: 'POST',
        body: {
          creditLimit: amount,
          ...body,
        },
      }),
      invalidatesTags: ['Members', 'Wallet'],
    }),

    changeStatus: builder.mutation<
      any,
      { userId: number; status: string; betlock?: boolean; transactionCode?: string }
    >({
      query: ({ userId, status, transactionCode, betlock }) => ({
        url: `/users/status/${userId}`,
        method: 'PATCH',
        body: { status, transactionCode, betlock },
      }),
      invalidatesTags: ['Members'],
    }),
    getSportPermission: builder.query<SportPermissionResponse, { userId: string | undefined }>({
      query: ({ userId }) => ({
        url: `/sports-permission/${userId}`,
        method: 'GET',
        params: { ts: Date.now() },
      }),
    }),
    updateSportPermission: builder.mutation<
      any,
      { userId: string; body: UpdateSportPermissionBody }
    >({
      query: ({ userId, body }) => ({
        url: `/sports-permission/${userId}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Members'],
    }),

    updateTransactionCode: builder.mutation<{ success: boolean; message: string }, void>({
      query: () => ({
        url: 'users/transaction-code/viewed',
        method: 'POST',
      }),
    }),

    //transaction history
    getTransactionHistory: builder.query<
      TransactionHistoryResponse,
      {
        page?: number;
        limit?: number;
        search?: string;
        fromDate?: string;
        toDate?: string;
        userId: number;
      }
    >({
      query: ({ search, fromDate, toDate, page, limit, userId }) => ({
        url: `users/transactions/deposit-withdraw/${userId}`,
        method: 'GET',
        params: {
          ...(search ? { search } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(userId ? { userId } : {}),
          ts: Date.now(),
        },
      }),
    }),

    getUserPlbyEvent: builder.query<
      EventProfitLossResponse,
      {
        searchByEvent?: string;
        sport?: string;
        fromDate?: string;
        toDate?: string;
        gameType?: string;
        page?: number;
        limit?: number;
        userId?: number;
      }
    >({
      query: ({ searchByEvent, sport, fromDate, toDate, gameType, userId, page, limit }) => ({
        url: '/reports/event/profitloss',
        method: 'GET',
        params: {
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(searchByEvent ? { searchByEvent } : {}),
          ...(sport ? { sport } : {}),
          ...(gameType ? { gameType } : {}),
          ...(userId ? { userId } : {}),
          ts: Date.now(),
        },
      }),
    }),
    getSummary: builder.query<
      AccountSummaryResponse,
      {
        username?: string;
        page?: number;
        limit?: number;
        rollId?: number;
        level?: number;
        fromDate?: string;
        toDate?: string;
        status?: string;
      }
    >({
      query: ({ username, page, limit, rollId, level, fromDate, toDate, status }) => ({
        url: '/users/summary',
        method: 'GET',
        params: {
          ...(username ? { username } : {}),
          ...(page ? { page } : {}),
          ...(limit ? { limit } : {}),
          ...(rollId ? { rollId } : {}),
          ...(level ? { level } : {}),
          ...(fromDate ? { fromDate } : {}),
          ...(toDate ? { toDate } : {}),
          ...(status ? { status } : {}),
          ts: Date.now(),
        },
      }),
    }),
    /**
     * The per-brand rail the super admin configured for this owner. Kept next to
     * `getMyRoles` because the sidebar is the only caller of either.
     */
    getAdminMenus: builder.query<{ menus: Record<string, boolean> }, void>({
      query: () => ({
        url: '/brand/admin-menus',
        method: 'GET',
      }),
    }),
    getMyRoles: builder.query<Role[], void>({
      query: () => ({
        url: '/role/my',
        method: 'GET',
      }),
      transformResponse: (response: Role[]) =>
        response.filter((role) => role.name !== 'DEMO' && role.name !== 'RESULT MANAGER'),
    }),
    getAvailableRolesByRoleId: builder.query<Role[], { roleId: number }>({
      query: ({ roleId }) => ({
        url: `/role/available-by-role/${roleId}`,
        method: 'GET',
      }),
      transformResponse: (response: Role[]) =>
        response.filter((role) => role.name !== 'DEMO' && role.name !== 'RESULT MANAGER'),
    }),
    getRoleAccessByUserId: builder.query<AccessRoleResponse, { userId: number }>({
      query: ({ userId }) => ({
        url: `/role/access-role/${userId}`,
        method: 'GET',
      }),
    }),
    updateExposureLimit: builder.mutation<
      { success: boolean; message: string },
      {
        amount: number;
        userId: string;
        transactionCode: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/wallets/update/exposure-limit`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Members', 'Wallet'],
    }),
    updateCreditLimit: builder.mutation<
      { success: boolean; message: string },
      {
        amount: number;
        userId: string;
        transactionCode: string;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/wallets/update/credit-limit`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Members', 'Wallet'],
    }),

    updateAdminSettings: builder.mutation<
      { success: boolean },
      {
        userId: string | number;
        depositCommissionPercent?: number;
        partnership?: number;
      }
    >({
      query: ({ userId, ...body }) => ({
        url: `users/${userId}/admin-settings`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Members'],
    }),
  }),
});

export const {
  useGetMembersQuery,
  useGetSubMembersQuery,
  useGetAccountSummaryQuery,
  useGetCasinoDetailQuery,
  useAddCreditRefrenceMutation,
  useChangeStatusMutation,
  useGetSportPermissionQuery,
  useUpdateSportPermissionMutation,
  useGetTransactionHistoryQuery,
  useUpdateTransactionCodeMutation,
  useGetUserPlbyEventQuery,
  useGetSummaryQuery,
  useGetMyRolesQuery,
  useGetAdminMenusQuery,
  useGetAvailableRolesByRoleIdQuery,
  useGetRoleAccessByUserIdQuery,
  useUpdateCreditLimitMutation,
  useUpdateExposureLimitMutation,
  useUpdateAdminSettingsMutation,
} = downlineApi;
